import moment from 'moment';
import React, { FC, useContext } from 'react';
import { renderedData } from '../../SearchFormComponent';
import { ContentContext } from '../../index.contexts';
import { StyledFlexiCalendarBoxDiv } from './flexibleDateCalendar.styles';
import { FlexibleCalendarType } from './flexibleDateCalendar.types';
import { usePageContext } from '../../../../../../context/PageContext';
import { checkLocaleToAddYears } from '../../../utils/DateUtils';

const FlexibleDateCalendar: FC<FlexibleCalendarType> = ({
  monthList,
  setDateHandler,
  singleDateLimit,
  useEnglishMomentForCalculation,
}) => {
  const { nightCount, setNightCount, monthSelected, setMonthSelected, yearSelected, setYearSelected } =
    useContext(renderedData);

  const { maxNumberOfNights, howManyNights, nightCountDecreaseLabel, nightCountIncreaseLabel } =
    useContext(ContentContext);

  const nightsLimit = maxNumberOfNights ? maxNumberOfNights : 9;
  let lastMonthToShow: number;
  let lastYeartoShow: number;
  const { currentLocale } = usePageContext();
  let latestMonth: number;
  let currentYear: number;
  let nextYear: number;

  if (useEnglishMomentForCalculation) {
    latestMonth = Number(moment().locale('en').format('M')) - 1;
    currentYear = Number(moment().locale('en').format('YYYY'));
    nextYear = currentYear + 1;
    lastMonthToShow = Number(moment(singleDateLimit).locale('en').format('M')) - 1;
    lastYeartoShow = Number(moment(singleDateLimit).locale('en').format('YYYY'));
  } else {
    latestMonth = Number(moment().format('M')) - 1;
    currentYear = Number(moment().format('YYYY'));
    nextYear = currentYear + 1;
    lastMonthToShow = Number(moment(singleDateLimit).format('M')) - 1;
    lastYeartoShow = Number(moment(singleDateLimit).format('YYYY'));
  }

  const clickTrackingLoc = 'Search Form';

  const onMonthSelection = (monthInd: number, year: number): void => {
    setYearSelected(year);
    setMonthSelected(monthInd);
    if (setDateHandler) {
      setDateHandler(monthInd, nightCount, year);
    }
  };

  const onNightCountAdd = (): void => {
    const nightsCountInFlexibleSearch = nightCount < Number(nightsLimit) ? nightCount + 1 : Number(nightsLimit);
    setNightCount(nightsCountInFlexibleSearch);
    if (setDateHandler) {
      setDateHandler(monthSelected, nightsCountInFlexibleSearch, yearSelected);
    }
  };

  const onNightCountDecr = (): void => {
    const nightsCountInFlexibleSearch = nightCount <= 1 ? 1 : nightCount - 1;
    setNightCount(nightsCountInFlexibleSearch);
    if (setDateHandler) {
      setDateHandler(monthSelected, nightsCountInFlexibleSearch, yearSelected);
    }
  };

  return (
    <StyledFlexiCalendarBoxDiv
      data-component-name="a-shop-FlexibleDateCalendar"
      data-testid="shop-FlexibleDateCalendar"
      className="search__flexible_calendar"
    >
      <div className="row mx-0 stepper-container">
        <div className="t-subtitle-m stepper-container__label">{howManyNights}</div>
        <div className="stepper-container__counter">
          <div>
            <button
              type="button"
              className={`${nightCount < 2 ? 'disable-btn' : ''} custom_click_track stepper-container__counter__btn`}
              {...{ custom_click_track_value: `${clickTrackingLoc}| Flexible Date Picker Day minus button |internal` }}
              aria-label={`${nightCountDecreaseLabel} ${nightCount < 2 ? 'Disabled' : 'Enabled'}`}
              onClick={onNightCountDecr}
              data-testid="decNightCount"
            >
              <span className="icon-stepper-minus t-title-s"></span>
            </button>
          </div>
          <div className="t-subtitle-xl" aria-live="polite">
            {nightCount}
          </div>
          <div>
            <button
              type="button"
              className={`${
                nightCount > Number(nightsLimit) - 1 ? 'disable-btn' : ''
              } custom_click_track stepper-container__counter__btn`}
              {...{
                custom_click_track_value: `${clickTrackingLoc}| Flexible Date Picker Day addition button |internal`,
              }}
              aria-label={`${nightCountIncreaseLabel} ${nightCount > Number(nightsLimit) - 1 ? 'Disabled' : 'Enabled'}`}
              onClick={onNightCountAdd}
              data-testid="incNightCount"
            >
              <span className="icon-stepper-plus t-title-s"></span>
            </button>
          </div>
        </div>
      </div>

      <div className="months-container">
        <div className="t-font-xs display-year">{checkLocaleToAddYears(currentLocale, currentYear)}</div>
        <div className="row mx-0">
          {monthList?.map((month, index) => {
            return (
              <div>
                {(Number(lastYeartoShow) === Number(currentYear)
                  ? index <= lastMonthToShow && index >= latestMonth
                  : index >= latestMonth) && (
                  <button
                    className={`custom_click_track m-button-secondary ${
                      monthSelected === index && yearSelected === currentYear ? 'selected-month' : ''
                    } months-cta`}
                    {...{
                      custom_click_track_value: `${clickTrackingLoc}| Flexible Date Picker Month Selected ${month} button |internal`,
                    }}
                    type="button"
                    onClick={(): void => onMonthSelection(index, currentYear)}
                    data-testid="months-cta-test"
                  >
                    {monthSelected === index && yearSelected === currentYear && (
                      <span className="icon-check check-mark"></span>
                    )}
                    <span>{month}</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {nextYear <= lastYeartoShow && (
          <>
            <div className="t-font-xs display-year">
              {Number(checkLocaleToAddYears(currentLocale, currentYear) + 1)}
            </div>
            <div className="row mx-0">
              {monthList?.map((month, index) => {
                return (
                  <div>
                    {index <= lastMonthToShow && (
                      <button
                        className={`custom_click_track m-button-secondary ${
                          monthSelected === index && yearSelected === nextYear ? 'selected-month' : ''
                        } months-cta`}
                        {...{
                          custom_click_track_value: `${clickTrackingLoc}| Flexible Date Picker Month Selected ${month} button |internal`,
                        }}
                        type="button"
                        onClick={(): void => onMonthSelection(index, nextYear)}
                      >
                        {monthSelected === index && yearSelected === nextYear && (
                          <span className="icon-check check-mark"></span>
                        )}
                        {month}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </StyledFlexiCalendarBoxDiv>
  );
};

export default FlexibleDateCalendar;
